/**
 * DefaultAgentProfileCard
 * Lets the admin set the name + avatar of the free agent every new user receives
 * at registration. Users can still rename / re-avatar it afterwards.
 */

import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useNotification } from '../../../../hooks/useNotification';
import { useAvatars } from '../../../../services/agentAvatar.service';
import {
  useDefaultAgentProfile,
  useUpdateDefaultAgentProfile,
} from '../../../../services/defaultAgentProfile.service';
import type { AgentAvatar } from '../types';

const NAME_MAX_LENGTH = 100;
const PICKER_LIMIT = 100;

const AvatarThumb: React.FC<{ avatar: AgentAvatar; selected: boolean; onSelect: () => void }> = ({
  avatar,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    title={avatar.name}
    onClick={onSelect}
    className={`h-14 w-14 shrink-0 overflow-hidden rounded-full border-2 bg-slate-100 p-0 dark:bg-slate-700 ${
      selected ? 'border-[#3B82F6] ring-2 ring-[#3B82F6]/40' : 'border-transparent'
    }`}
  >
    {avatar.previewUrl && avatar.type === 'video' ? (
      <video src={avatar.previewUrl} className="h-full w-full object-cover" muted />
    ) : avatar.previewUrl ? (
      <img src={avatar.previewUrl} alt={avatar.name} className="h-full w-full object-cover" />
    ) : null}
  </button>
);

export const DefaultAgentProfileCard: React.FC = () => {
  const notification = useNotification();
  const { data: profile, isLoading: profileLoading } = useDefaultAgentProfile();
  const { data: avatarList, isLoading: avatarsLoading } = useAvatars({
    status: 'active',
    limit: PICKER_LIMIT,
  });
  const updateProfile = useUpdateDefaultAgentProfile();

  const [name, setName] = useState('');
  const [avatarPublicId, setAvatarPublicId] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    setName(profile.name);
    setAvatarPublicId(profile.avatarPublicId);
  }, [profile]);

  const avatars = avatarList?.data ?? [];
  // Default-flagged avatars first, matching the picker users see.
  const sortedAvatars = [...avatars].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  const trimmedName = name.trim();
  const isDirty =
    !!profile && (trimmedName !== profile.name || avatarPublicId !== profile.avatarPublicId);

  const handleSave = () => {
    updateProfile.mutate(
      { name: trimmedName, avatarPublicId },
      {
        onSuccess: () => notification.success('Default agent updated'),
        onError: (error) =>
          notification.error(
            error instanceof Error ? error.message : 'Failed to update default agent'
          ),
      }
    );
  };

  return (
    <Box
      className="mb-6 rounded-xl p-4 shadow-sm"
      sx={{
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-slate-100">
        Default agent for new users
      </Typography>
      <Typography variant="body2" className="mb-4 text-gray-500 dark:text-slate-400">
        Every newly registered user gets one free agent with this name and avatar. Users can change
        both later in their agent management screen.
      </Typography>

      {profileLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Box className="flex flex-col gap-4">
          <TextField
            label="Agent name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
            error={!trimmedName}
            helperText={!trimmedName ? 'Name is required' : `${name.length}/${NAME_MAX_LENGTH}`}
            className="max-w-md"
          />

          <Box>
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              Avatar
            </Typography>
            <Box className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setAvatarPublicId(null)}
                className={`h-14 w-14 shrink-0 rounded-full border-2 text-xs text-gray-500 dark:text-slate-400 ${
                  avatarPublicId === null
                    ? 'border-[#3B82F6] ring-2 ring-[#3B82F6]/40'
                    : 'border-dashed border-gray-300 dark:border-slate-600'
                }`}
              >
                None
              </button>
              {avatarsLoading && <CircularProgress size={20} />}
              {sortedAvatars.map((avatar) => (
                <AvatarThumb
                  key={avatar.publicId}
                  avatar={avatar}
                  selected={avatar.publicId === avatarPublicId}
                  onSelect={() => setAvatarPublicId(avatar.publicId)}
                />
              ))}
            </Box>
            {profile?.avatarPublicId && profile.avatar && !profile.avatar.isActive && (
              <Typography variant="caption" color="error">
                The saved avatar is inactive, so new users currently get no avatar.
              </Typography>
            )}
          </Box>

          <Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty || !trimmedName || updateProfile.isPending}
              sx={{
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {updateProfile.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
